import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { chatApi } from "../../api/chat";
import type { ChatChannel, ChatMessage, ChatPerson } from "../../api/chat";
import { useChat } from "../../components/chat/useChatConnection";
import { Select } from "../../components/Select";
import type { SelectOption } from "../../components/Select";
import { useToastError } from "../../components/Toast";
import Ic from "../../components/icons.jsx";
import { ChatAvatar } from "./ChatAvatar";
import { dateTimeOf, foundWhen, searchWords } from "./chatFormat";
import { Marked, MessageBody } from "./MessageBody";

/** A search: the words, the channel or conversation to look in, and whose messages; empty means any. */
export type SearchQuery = { q: string; channelId: string; authorId: string };

/** Where a found message opens: its channel at the message, or for a reply, the channel at its message and the thread. */
export function foundLink(m: ChatMessage): string {
  return m.parentId ? `/chat/${m.channelId}?m=${m.parentId}&reply=${m.id}` : `/chat/${m.channelId}?m=${m.id}`;
}

type Found = { status: "idle" | "loading" | "done" | "failed"; total: number; messages: ChatMessage[] };

/**
 * Message search, Slack's way: what was found takes the conversation's place, newest first, with the words marked.
 * A result opens its channel at the message, a reply its thread. The query lives in the address, so Back from a
 * message comes back here; typing searches a moment after it stops.
 */
export function SearchResults({ query, narrow, onChange }: {
  query: SearchQuery;
  narrow: boolean;
  /** A new query; `replace` keeps refining it from adding a step to Back. */
  onChange: (next: SearchQuery, replace: boolean) => void;
}) {
  const { t } = useTranslation();
  const { channels, people, me } = useChat();
  const navigate = useNavigate();
  const toastError = useToastError();
  const [text, setText] = useState(query.q);
  const [found, setFound] = useState<Found>({ status: "idle", total: 0, messages: [] });
  const [loadingMore, setLoadingMore] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  // The words last sent to the address: a change from elsewhere (a new search from the channel list) replaces the text.
  const sent = useRef(query.q);
  const asked = useRef(0);
  const words = useMemo(() => searchWords(query.q), [query.q]);
  const ready = words.length > 0 || query.channelId !== "" || query.authorId !== "";
  const { q, channelId, authorId } = query;

  useEffect(() => {
    if (!narrow) field.current?.focus();
  }, [narrow]);

  useEffect(() => {
    if (q !== sent.current) {
      sent.current = q;
      setText(q);
    }
  }, [q]);

  useEffect(() => {
    if (text === sent.current) return;
    const timer = window.setTimeout(() => {
      sent.current = text;
      onChange({ q: text, channelId, authorId }, true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [text, channelId, authorId, onChange]);

  useEffect(() => {
    const n = ++asked.current;
    if (!ready) {
      setFound({ status: "idle", total: 0, messages: [] });
      return;
    }
    setFound((f) => ({ ...f, status: "loading" }));
    chatApi.search({ q, channelId: channelId || undefined, authorId: authorId || undefined })
      .then((r) => { if (asked.current === n) setFound({ status: "done", total: r.total, messages: r.messages }); })
      .catch(() => { if (asked.current === n) setFound({ status: "failed", total: 0, messages: [] }); });
  }, [q, channelId, authorId, ready]);

  async function loadMore() {
    const last = found.messages[found.messages.length - 1];
    if (!last || loadingMore) return;
    const n = asked.current;
    setLoadingMore(true);
    try {
      const r = await chatApi.search({ q, channelId: channelId || undefined, authorId: authorId || undefined, before: last.id });
      if (asked.current !== n) return;
      setFound((f) => {
        const seen = new Set(f.messages.map((m) => m.id));
        return { ...f, messages: [...f.messages, ...r.messages.filter((m) => !seen.has(m.id))] };
      });
    } catch (e) {
      toastError(e);
    } finally {
      setLoadingMore(false);
    }
  }

  function submit() {
    sent.current = text;
    onChange({ q: text, channelId, authorId }, true);
  }

  const byId = new Map((channels ?? []).map((c) => [c.id, c]));
  const nameOf = (id: string) => people.get(id)?.name ?? t("chat.unknownPerson", "former colleague");
  const places: SelectOption[] = [
    { value: "", label: t("chat.searchEverywhere", "Everywhere") },
    ...(channels ?? []).filter((c) => !c.directWith).map((c) => ({
      value: c.id, label: c.name, left: c.privateChannel ? <Ic.lock /> : <Ic.hash />, group: t("chat.channels", "Channels"),
    })),
    ...(channels ?? []).filter((c) => c.directWith)
      .map((c) => ({ value: c.id, label: nameOf(c.directWith!), left: <Ic.user />, group: t("chat.directs", "Direct messages") }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const authors: SelectOption[] = [
    { value: "", label: t("chat.searchAnyone", "From anyone") },
    ...[...people.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ value: p.id, label: p.id === me ? t("chat.searchMe", "{{name}} (you)", { name: p.name }) : p.name })),
  ];
  const filtered = channelId !== "" || authorId !== "";
  const typedWords = searchWords(text);
  const now = new Date();

  return (
    <section className="chat-main chat-search" aria-label={t("chat.search", "Search")}>
      <header className="chat-head chat-search-head">
        {narrow && (
          <button type="button" className="chat-tool chat-back" onClick={() => navigate("/chat")}
            aria-label={t("chat.back", "Back to channels")}>
            <Ic.arrowLeft />
          </button>
        )}
        <form className="chat-search-field" role="search" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <Ic.search />
          <input
            ref={field}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("chat.searchPlaceholder", "Search messages")}
            aria-label={t("chat.searchPlaceholder", "Search messages")}
            enterKeyHint="search"
          />
          {text && (
            <button type="button" className="chat-tool sm" onClick={() => { setText(""); field.current?.focus(); }}
              title={t("chat.searchClear", "Clear")} aria-label={t("chat.searchClear", "Clear")}>
              <Ic.x />
            </button>
          )}
        </form>
        {!narrow && (
          <button type="button" className="btn sm" onClick={() => navigate("/chat")}>
            {t("chat.searchClose", "Close search")}
          </button>
        )}
      </header>

      <div className="chat-search-bar">
        <div className="chat-search-pick">
          <Select
            value={channelId}
            options={places}
            onChange={(v) => onChange({ q: text, channelId: v, authorId }, true)}
            ariaLabel={t("chat.searchIn", "Where")}
          />
        </div>
        <div className="chat-search-pick">
          <Select
            value={authorId}
            options={authors}
            onChange={(v) => onChange({ q: text, channelId, authorId: v }, true)}
            ariaLabel={t("chat.searchFrom", "From")}
          />
        </div>
        <span className="chat-search-count" aria-live="polite">
          {found.status === "done" && found.total > 0 && t("chat.searchCount", "{{count}} messages", { count: found.total })}
        </span>
      </div>

      <div className="chat-search-scroll">
        {!ready && (
          <div className="chat-search-state">
            <div className="chat-intro-mark"><Ic.search /></div>
            <h2>{t("chat.searchStartTitle", "Search messages")}</h2>
            <p>
              {text.trim() && typedWords.length === 0
                ? t("chat.searchShort", "Type at least two letters.")
                : t("chat.searchStartText", "Type a word from a message, a file's name or a colleague's name. č, ć, š, ž and đ can be typed without their marks.")}
            </p>
          </div>
        )}
        {ready && found.status === "loading" && found.messages.length === 0 && (
          <div className="chat-hits" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="chat-hit skeleton">
                <span className="chat-skel line short" />
                <span className="chat-skel line" />
                <span className="chat-skel line" />
              </div>
            ))}
          </div>
        )}
        {found.status === "failed" && <p className="chat-error chat-pad">{t("chat.searchFailed", "The search did not work. Try again.")}</p>}
        {found.status === "done" && found.total === 0 && (
          <div className="chat-search-state">
            <div className="chat-intro-mark"><Ic.search /></div>
            <h2>{t("chat.searchNoneTitle", "Nothing found")}</h2>
            <p>
              {q.trim()
                ? t("chat.searchNoneText", "No message has “{{q}}”. Check the letters or try a shorter word.", { q: q.trim() })
                : t("chat.searchNoneHere", "No messages here.")}
            </p>
            {filtered && (
              <button type="button" className="btn sm" onClick={() => onChange({ q: text, channelId: "", authorId: "" }, true)}>
                {t("chat.searchClearFilters", "Look everywhere, from anyone")}
              </button>
            )}
          </div>
        )}
        {found.messages.length > 0 && (
          <ol className={`chat-hits${found.status === "loading" ? " stale" : ""}`}>
            {found.messages.map((m) => (
              <li key={m.id}>
                <Link className="chat-hit" to={foundLink(m)} state={{ fromSearch: true }}>
                  <div className="chat-hit-where">
                    <Where channel={byId.get(m.channelId)} people={people} />
                    {m.parentId && <span className="chat-hit-thread">· {t("chat.searchInThread", "reply in a thread")}</span>}
                    <time className="chat-hit-time" dateTime={m.createdAt} title={dateTimeOf(m.createdAt)}>
                      {foundWhen(m.createdAt, now)}
                    </time>
                  </div>
                  <div className="chat-hit-msg">
                    <ChatAvatar person={people.get(m.authorId)} id={m.authorId} />
                    <div className="chat-hit-main">
                      <span className="chat-msg-author">{people.get(m.authorId)?.name ?? t("chat.unknownPerson", "former colleague")}</span>
                      <Clamp>
                        <MessageBody body={m.body} people={people} me={me} words={words} plain />
                      </Clamp>
                      {m.attachments.length > 0 && (
                        <div className="chat-hit-files">
                          {m.attachments.map((a) => (
                            <span key={a.id} className="chat-hit-file">
                              {a.contentType.startsWith("image/") ? <Ic.image /> : <Ic.pdf />}
                              <span><Marked text={a.filename} words={words} /></span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        )}
        {found.messages.length > 0 && found.messages.length < found.total && (
          <div className="chat-search-more">
            <button type="button" className="btn sm" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? t("chat.loading", "Loading…") : t("chat.searchMore", "Show more")}
            </button>
          </div>
        )}
      </div>
    </section>
  );

}

/** Where a found message was written: a channel by its sign and name, a conversation by the colleague. */
export function Where({ channel, people }: { channel: ChatChannel | undefined; people: Map<string, ChatPerson> }) {
  const { t } = useTranslation();
  if (!channel) return <span className="chat-hit-name">…</span>;
  if (channel.directWith) {
    const person = people.get(channel.directWith);
    return (
      <>
        <ChatAvatar person={person} id={channel.directWith} size="xs" />
        <span className="chat-hit-name">{person?.name ?? t("chat.unknownPerson", "former colleague")}</span>
      </>
    );
  }
  return (
    <>
      {channel.privateChannel ? <Ic.lock /> : <Ic.hash />}
      <span className="chat-hit-name">{channel.name}</span>
    </>
  );
}

/** A long message shows its first lines among the results, the rest fading out. */
export function Clamp({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const el = box.current;
    if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, []);
  return <div ref={box} className={`chat-hit-body${clipped ? " clipped" : ""}`}>{children}</div>;
}
